export type ContractPlan = {
  name: string;
};

/**
 * Famílias de plano elegíveis para linha nova/Aditivo sem o prefixo NV/AD no nome.
 * Extensível: inclua padrões aqui quando novas famílias forem cadastradas.
 */
const ADITIVO_PLAN_FAMILY_PATTERNS: ReadonlyArray<RegExp> = [/\bTIM\s+OFFICE\b/i];

export function isAditivoEligiblePlanName(planName: string | null | undefined): boolean {
  if (!planName?.trim()) return false;

  if (planName.includes('NV/AD')) return true;

  return ADITIVO_PLAN_FAMILY_PATTERNS.some((pattern) => pattern.test(planName));
}

export function isWttxPlan(planName: string | null | undefined): boolean {
  if (!planName?.trim()) return false;

  const normalizedName = planName.toUpperCase();
  return (
    normalizedName.includes('WTTX') ||
    normalizedName.includes('TIM EMPRESA INTERNET') ||
    normalizedName.includes('5MB')
  );
}

export function isPlanCompatibleWithNegociacao(planName: string, negociacao: string): boolean {
  if (negociacao === 'Aditivo') {
    return isAditivoEligiblePlanName(planName) || isWttxPlan(planName);
  }
  if (negociacao === 'Renegociação') return planName.includes('RP');
  return true;
}

export function filterPlansForContractLine<T extends ContractPlan>(
  plans: T[],
  negociacao: string,
  isWttxHandset: boolean
): T[] {
  if (isWttxHandset) {
    return plans.filter((plan) => isWttxPlan(plan.name));
  }

  return plans.filter((plan) => isPlanCompatibleWithNegociacao(plan.name, negociacao));
}

export function filterAditivoEligiblePlans<T extends ContractPlan>(plans: T[]): T[] {
  return filterPlansForContractLine(plans, 'Aditivo', false);
}

/** Rótulo de franquia para selects de plano (ex.: TIM Office com 0 GB de dados). */
export function formatContractPlanDataAllowanceLabel(dataAllowanceGb: number): string {
  if (!Number.isFinite(dataAllowanceGb) || dataAllowanceGb <= 0) {
    return 'Sem franquia de dados';
  }

  const formatted =
    Math.abs(dataAllowanceGb % 1) < 1e-9
      ? String(dataAllowanceGb)
      : dataAllowanceGb.toFixed(1);

  return `${formatted} GB`;
}

export function formatContractPlanMonthlyPrice(price: number): string {
  if (!Number.isFinite(price)) return '—';
  return price.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatContractPlanOptionLabel(plan: {
  name: string;
  dataAllowanceGb: number;
  totalMonthlyPrice: number;
}): string {
  const franquia = formatContractPlanDataAllowanceLabel(plan.dataAllowanceGb);
  const valor = formatContractPlanMonthlyPrice(plan.totalMonthlyPrice);
  return `${plan.name} - ${franquia} - R$ ${valor}/mês`;
}

/** Valor mensal informado (inclui 0 ou negativo quando o plano tiver benefício/desconto). */
export function hasContractLineMonthlyValue(
  valor: number | string | undefined | null
): boolean {
  if (valor == null) return false;
  if (typeof valor === 'string' && valor.trim() === '') return false;
  return Number.isFinite(Number(valor));
}

export function resolvePlanTotalMonthlyPrice(
  totalMonthlyPrice: number | undefined | null
): number {
  if (totalMonthlyPrice == null || !Number.isFinite(totalMonthlyPrice)) return 0;
  return totalMonthlyPrice;
}
